import { Edit, Visibility } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import { generatePath, useNavigate } from 'react-router-dom';
import { OperationObject } from 'system/utils/event-batch/filters-object-to-query-string-batch-event';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Tooltip } from '@mui/material';
import {
  MunicipalityListVM,
  ResearchMunicipalityForm,
} from 'civil-status/view-models/research-municipality';
import { ID_DOMINIO_COMUNI } from 'civil-status/pages/research-municipality';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useFilterTable } from 'shell/hooks/use-filter-table';

interface TableMunicipalityProps {
  data?: Array<MunicipalityListVM>;
  filterSelected?: OperationObject;
  getMunicipalityListData: (data: OperationObject, page?: number, rowsPerPage?: number) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
  isAuthorizationEdit?: boolean;
  isAuthorizationDelete?: boolean;
}

const TableMunicipalityComponent = ({
  data,
  filterSelected,
  getMunicipalityListData,
  tableProps = {},
  setTableProps,
  isAuthorizationEdit,
  isAuthorizationDelete,
}: TableMunicipalityProps): ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<MunicipalityListVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<MunicipalityListVM>>({
    element: null,
    open: false,
  });

  const navigate = useNavigate();
  const filterTable = useFilterTable<ResearchMunicipalityForm>();

  const onEditPress = useEventCallback((item: MunicipalityListVM) => {
    if (!item.id) {
      return;
    }

    navigate({
      pathname: generatePath('/stato-civile/ricerca-comune/modifica/:idValore', {
        idValore: item.id,
      }),
    });
  });

  const onVisibilityPress = useEventCallback((item: MunicipalityListVM) => {
    if (!item.id) {
      return;
    }

    navigate({
      pathname: generatePath('/stato-civile/ricerca-comune/visualizza/:idValore', {
        idValore: item.id,
      }),
    });
  });

  const deleteData = useHttpClient(``, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const onTrashPress = useEventCallback((item: MunicipalityListVM) => {
    if (!item.id) {
      return;
    }

    setDeleteAlertModal({ open: true, element: item });
  });

  const body: Array<MunicipalityListVM> = useMemo(() => {
    return (data || []).map((item) => {
      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onVisibilityPress(item);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
            <Tooltip title={isAuthorizationEdit ? '' : 'Operatore non autorizzato'}>
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEditPress(item);
                  }}
                  aria-label="Modifica elemento"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                  disabled={!isAuthorizationEdit}
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isAuthorizationDelete ? '' : 'Operatore non autorizzato'}>
              <span>
                <IconButton
                  color="error"
                  aria-label="elimina"
                  component="span"
                  onClick={() => onTrashPress(item)}
                  disabled={!isAuthorizationDelete}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        ),
      };
    });
  }, [data]);

  const onChangePage = useEventCallback((page: number) => {
    getMunicipalityListData(filterSelected || {}, page, tableProps.rowsPerPage);

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
    filterTable.setValues({
      ...(filterTable?.values || {}),
      orders: filterTable?.values?.orders || {},
      page: page || 0,
      rowsPerPage: tableProps.rowsPerPage || 10,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getMunicipalityListData(filterSelected || {}, 0, rowsPerPage);

    setTableProps({
      page: 0,
      rowsPerPage,
    });

    filterTable.setValues({
      ...(filterTable?.values || {}),
      orders: filterTable?.values?.orders || {},
      page: 0,
      rowsPerPage: rowsPerPage || 10,
    });
  });

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista comuni"
            page={tableProps.page || 0}
            rowsPerPage={tableProps.rowsPerPage || 10}
            body={body}
            count={(body && body[0]?.totalCount) || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
            isServer
          />
        </Grid>
      </Grid>

      <DeleteAlertModal
        title="Cancellazione Comune"
        message={`Confermare la cancellazione del comune ${
          deleteAlertModal.element?.description || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteData({
              url: `${__ACT_API_URL__}/valori-dominio-atti/delete/${ID_DOMINIO_COMUNI}/${deleteAlertModal.element?.id}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Comune"
        message={`La cancellazione del comune ${
          deleteSuccessModal.element?.description || ''
        } è avvenuta con successo`}
        onClose={() => {
          getMunicipalityListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          getMunicipalityListData(filterSelected || {}, tableProps.page, tableProps.rowsPerPage);
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </>
  );
};

TableMunicipalityComponent.displayName = 'Municipality List - Table component';

const TableMunicipality = memo(TableMunicipalityComponent);

export default TableMunicipality;
